import React, { useEffect, useRef, useState } from "react";

/**
 * Reveal — fades + slides children in when scrolled into view.
 *
 * Props:
 *  - delay (ms)   default 0
 *  - duration (ms) default 800
 *  - y (px)       default 28 (vertical offset before reveal)
 *  - x (px)       default 0
 *  - once         default true
 *  - as           default "div" (any element/component)
 *  - threshold    default 0.15
 */
export default function Reveal({
  children,
  delay = 0,
  duration = 800,
  y = 28,
  x = 0,
  once = true,
  threshold = 0.15,
  as: Tag = "div",
  className = "",
  style = {},
  ...rest
}) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setVisible(true);
            if (once) obs.disconnect();
          } else if (!once) {
            setVisible(false);
          }
        });
      },
      { threshold, rootMargin: "0px 0px -8% 0px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [once, threshold]);

  return (
    <Tag
      ref={ref}
      className={className}
      style={{
        ...style,
        opacity: visible ? 1 : 0,
        transform: visible
          ? "translate3d(0,0,0)"
          : `translate3d(${x}px, ${y}px, 0)`,
        transition: `opacity ${duration}ms cubic-bezier(.22,.61,.36,1) ${delay}ms, transform ${duration}ms cubic-bezier(.22,.61,.36,1) ${delay}ms`,
        willChange: "opacity, transform",
      }}
      {...rest}
    >
      {children}
    </Tag>
  );
}
